
import IconEdit from "@/components/common/icon/IconEdit.vue";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import Pagination from "@/components/common/Pagination.vue";
import { ISubclient, ISubclientData, ISubclientParams } from "@/entities/subclient";
import VueFilters from "@/vue-filters";
import { BvTableFieldArray } from "bootstrap-vue";
import { Component, Vue } from "vue-property-decorator";
import { Action } from "vuex-class";
import {
  EDirection,
  IInformationOptionsSubclients,
  IPageResponse,
  ISubclientsReportRequest,
} from "../../services/backend";
import { EventBus } from "@/event-bus";
import SubclientFilter from "@/components/subclient/SubclientFilter.vue";
import { Dictionary } from "vue-router/types/router";
import { i18n } from "@/i18n";
import { SweetAlertResult } from "sweetalert2";

@Component({
  name: "subclients-table",
  mixins: [VueFilters],
  components: {
    SubclientFilter,
    IconEdit,
    IconTrash,
    Pagination,
  },
})
export default class SubclientsTable extends Vue {
  @Action("fetchSubclientsPage") fetchSubclientsPage!: (
    params: ISubclientParams
  ) => Promise<IPageResponse<ISubclientData> | undefined>;
  @Action("updateStatusSubclient") updateStatusSubclient!: (id: number) => Promise<void>;
  @Action("generateSubclientsReport") generateSubclientsReport!: (
    request: ISubclientsReportRequest
  ) => Promise<void>;

  private subclients: IPageResponse<ISubclient> | undefined = {
    content: [] as ISubclient[],
  } as IPageResponse<ISubclient>;

  private queryParams = this.$route.query;
  private params: ISubclientParams = {
    active: this.$ability.can("read", "SubclientEditActive")
      ? this.queryParams.active !== "false"
      : true,
    clientDesc: this.queryParams.clientDesc?.toString(),
    codeSol: this.queryParams.codeSol?.toString(),
    countryId: this.queryParams.countryId ? Number(this.queryParams.countryId) : undefined,
    delegationDesc: this.queryParams.delegationDesc?.toString(),
    description: this.queryParams.description?.toString(),
    idSmartcash: this.queryParams.idSmartcash?.toString(),
    clientPrecise: this.queryParams.clientPrecise?.toString() === "true" || false,
    delegationPrecise: this.queryParams.delegationPrecise?.toString() === "true" || false,
    codePrecise: this.queryParams.codePrecise?.toString() === "true" || false,
    descriptionPrecise: this.queryParams.descriptionPrecise?.toString() === "true" || false,
  };

  private pageNumber = Number(this.queryParams.page) || 1;
  private pageSize = 10;
  private totalElements = 0;
  private orderBy = this.queryParams.orderBy?.toString() || "id";
  private sortDesc = this.queryParams.direction !== "ASC";

  constructor() {
    super();
  }

  mounted(): void {
    this.getSubclients();

    EventBus.$on("update-subclient-table", () => {
      this.onChangeCurrentPage(1);
    });
  }

  async beforeDestroy(): Promise<void> {
    EventBus.$off("update-subclient-table");
  }

  async onChangeCurrentPage(currentPage: number): Promise<void> {
    this.pageNumber = currentPage;
    await this.getSubclients();
  }

  async filterSubclients(): Promise<void> {
    this.$nextTick(async () => this.getSubclients());
  }

  async getSubclients(): Promise<void> {
    this.params.size = this.pageSize;
    this.params.orderBy = this.orderBy || undefined;
    this.params.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
    this.params.page = this.pageNumber;
    await this.$router
      .replace({ path: this.$route.path, query: this.params as Dictionary<string> })
      .catch(() => {
        // do nothing.
      });
    this.params.page = this.pageNumber - 1;

    this.subclients = await this.fetchSubclientsPage(this.params);

    this.totalElements = this.subclients?.totalElements || 0;
  }

  get showInactive(): boolean {
    return !this.params.active;
  }

  set showInactive(value: boolean) {
    this.params.active = !value;
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first",
      },
      {
        key: "id",
        label: "id",
        sortable: true,
        thClass: "d-none",
        tdClass: "d-none",
      },
      {
        key: "delegation.description",
        label: this.$t("column.delegation") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "client.codeSol,client.description",
        label: this.$t("column.client") as string,
        formatter: (_value: string, _key: string, item: ISubclient): string => {
          return `${item.client?.codeSol} - ${item.client?.description}`;
        },
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "codeSol",
        label: this.$t("column.code") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "description",
        label: this.$t("column.description") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "active",
        label: this.$t("column.active") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        thAttr: this.$ability.can("read", "SubclientEditActive") ? null : { hidden: true },
        tdAttr: this.$ability.can("read", "SubclientEditActive") ? null : { hidden: true },
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last",
      },
    ];
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("label.confirmation.to.export.message").toString(),
      text: this.$t("label.confirmation.to.export.message.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  async updateStatusSubclientLocal(subclient: ISubclient): Promise<void> {
    subclient.active = !subclient.active;
    await this.updateStatusSubclient(subclient.id!);
    await this.getSubclients();
  }

  get serviceSubclientsReportParams(): ISubclientsReportRequest {
    return {
      cashSubclientParamsDTO: this.params,
      informationOptionsSubclient: this.informationOptionsSubclients,
    } as ISubclientsReportRequest;
  }

  private async executeReportSubclient(): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();

    if (isConfirmed) {
      await this.generateSubclientsReport(this.serviceSubclientsReportParams);
      this.makeToast("success", this.$t("message.usersreport.ok").toString());
    }
  }

  private get informationOptionsSubclients(): IInformationOptionsSubclients {
    return {
      country: true,
      active: true,
      entity: true,
      entityDescription: true,
      center: true,
      centerDescription: true,
      delegation: true,
    } as IInformationOptionsSubclients;
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }
}
